import { h, forceUpdate } from "@stencil/core";
import { isBreakpointEqualOrLarger, isBreakpointSmaller, setScrollLock } from "../../../utils";
export class DcUiHeaderMainNav {
    constructor() {
        /**
         * The maximum number of entries in the main navigation before overflow is shown.
         * @default 11
         */
        this.maxEntries = 11;
        this.overflowNextLabel = 'Next';
        this.overflowPrevLabel = 'Previous';
        this.isOpen = false;
        this.isMobile = this.checkIfMobile();
        this.navOverflow = false;
        this.handleLanguageButtonMoved = () => {
            // Handle the event, e.g., force update or any other logic
            forceUpdate(this.el);
        };
        this.handleResize = () => {
            var _a;
            const wasMobile = this.isMobile;
            this.isMobile = this.checkIfMobile();
            // Check for overflow on resize
            this.checkForOverflow();
            // If breakpoint changed, close navigation and flyouts
            if (wasMobile !== this.isMobile) {
                // Close the mobile navigation if it's open
                if (this.isOpen) {
                    this.isOpen = false;
                    setScrollLock(false);
                    (_a = this.burger) === null || _a === void 0 ? void 0 : _a.dcToggleBurger();
                }
                // Close all flyouts by finding all active nav items and removing their active state
                const activeNavItems = this.el.querySelectorAll('dc-ui-header-main-nav-item');
                activeNavItems.forEach((item) => {
                    if (item.classList.contains('is-active')) {
                        // Access the component instance to call its closeFlyout method
                        const itemComponent = item;
                        if (typeof itemComponent.closeFlyout === 'function') {
                            itemComponent.closeFlyout();
                        }
                    }
                });
            }
        };
        this.handleScroll = () => {
            this.updateButtonStates();
        };
    }
    handleWindowClick(event) {
        var _a;
        if (!this.isOpen)
            return;
        const path = event.composedPath();
        const excludedComponents = [
            'dc-ui-header-burger',
            'dc-ui-header-language',
            'dc-ui-header-user-menu',
        ];
        // Check if click is within any excluded component
        const isExcludedComponent = excludedComponents.some((selector) => path.some((element) => element instanceof HTMLElement && element.tagName.toLowerCase() === selector.toLowerCase()));
        if (!path.includes(this.el) && !isExcludedComponent) {
            this.isOpen = false;
            setScrollLock(false);
            (_a = this.burger) === null || _a === void 0 ? void 0 : _a.dcToggleBurger();
        }
    }
    handleBurgerClick(event) {
        this.isOpen = event.detail;
        setScrollLock(event.detail);
    }
    handleKeyDown(event) {
        var _a;
        if (event.key === 'Escape' && this.isOpen) {
            this.isOpen = false;
            setScrollLock(false);
            (_a = this.burger) === null || _a === void 0 ? void 0 : _a.dcToggleBurger();
        }
    }
    componentDidLoad() {
        var _a;
        // Try to find burger in closest header
        const headerEl = this.el.closest('dc-ui-header');
        this.burger = headerEl === null || headerEl === void 0 ? void 0 : headerEl.querySelector('dc-ui-header-burger');
        // If not found, fallback to regular DOM query
        if (!this.burger) {
            this.burger = document.querySelector('dc-ui-header-burger');
        }
        window.addEventListener('resize', this.handleResize);
        this.el.addEventListener('languageButtonMoved', this.handleLanguageButtonMoved);
        if (this.maxEntries) {
            this.el.style.setProperty('--default-max-entries', this.maxEntries.toString());
        }
        // Set up the slot observer
        this.setupSlotObserver();
        // Add scroll event listener
        (_a = this.mainNavContentWrapperInner) === null || _a === void 0 ? void 0 : _a.addEventListener('scroll', this.handleScroll);
    }
    componentDidRender() {
        this.checkForOverflow();
        this.updateButtonStates();
    }
    componentDidUpdate() {
        this.checkForOverflow();
        this.updateButtonStates();
    }
    disconnectedCallback() {
        var _a;
        setScrollLock(false);
        window.removeEventListener('resize', this.handleResize);
        this.el.removeEventListener('languageButtonMoved', this.handleLanguageButtonMoved);
        // Disconnect the slot observer
        if (this.slotObserver) {
            this.slotObserver.disconnect();
        }
        // Remove scroll event listener
        (_a = this.mainNavContentWrapperInner) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', this.handleScroll);
    }
    async checkForOverflow() {
        // only check for overflow on desktop
        if (isBreakpointEqualOrLarger('xl')) {
            let navItemsWidth = 0;
            const navItems = this.el.querySelectorAll('dc-ui-header-main-nav-item, dc-ui-header-main-nav-link');
            const maxWidth = this.el.clientWidth - 64 - 64 - 8; // 32px padding on each side and 32px button width
            const gap = 4; // 4px gap between items
            navItems.forEach((item, index) => {
                const itemWidth = item.clientWidth + (index < navItems.length - 1 ? gap : 0);
                navItemsWidth += itemWidth;
            });
            this.navOverflow = navItemsWidth > maxWidth;
        }
        else {
            this.navOverflow = false;
        }
        this.updateButtonStates();
    }
    checkIfMobile() {
        return isBreakpointSmaller('xl');
    }
    scrollNav(direction) {
        const scrollAmount = this.mainNavContentWrapperInner.clientWidth;
        if (direction === 'next') {
            this.mainNavContentWrapperInner.scrollTo({
                left: this.mainNavContentWrapperInner.scrollLeft + scrollAmount,
                behavior: 'smooth',
            });
        }
        else {
            this.mainNavContentWrapperInner.scrollTo({
                left: this.mainNavContentWrapperInner.scrollLeft - scrollAmount,
                behavior: 'smooth',
            });
        }
        // Remove the setTimeout and add scroll event listeners
        if ('onscrollend' in this.mainNavContentWrapperInner) {
            // Modern browsers
            const scrollEndHandler = () => {
                this.updateButtonStates();
                this.mainNavContentWrapperInner.removeEventListener('scrollend', scrollEndHandler);
            };
            this.mainNavContentWrapperInner.addEventListener('scrollend', scrollEndHandler);
        }
        else {
            // Fallback for older browsers and Safari
            let scrollTimeout;
            const scrollHandler = () => {
                clearTimeout(scrollTimeout);
                scrollTimeout = window.setTimeout(() => {
                    this.updateButtonStates();
                    this.mainNavContentWrapperInner.removeEventListener('scroll', scrollHandler);
                }, 150);
            };
            this.mainNavContentWrapperInner.addEventListener('scroll', scrollHandler);
        }
    }
    async updateButtonStates() {
        const wrapper = this.mainNavContentWrapperInner;
        const scrollThreshold = 10; // increased threshold for better tolerance
        if (this.prevButton) {
            this.prevButton.setAttribute('disabled', wrapper.scrollLeft <= scrollThreshold ? 'true' : 'false');
        }
        if (this.nextButton) {
            const remainingScroll = wrapper.scrollWidth - (wrapper.scrollLeft + wrapper.clientWidth);
            this.nextButton.setAttribute('disabled', remainingScroll <= scrollThreshold ? 'true' : 'false');
        }
    }
    setupSlotObserver() {
        // Create mutation observer to watch for changes in slot content
        this.slotObserver = new MutationObserver(() => {
            // Check if all nav items are hydrated
            const navItems = this.el.querySelectorAll('dc-ui-header-main-nav-item, dc-ui-header-main-nav-link');
            const allHydrated = Array.from(navItems).every((item) => item.classList.contains('hydrated'));
            if (allHydrated) {
                this.checkForOverflow();
                this.updateButtonStates();
            }
        });
        // Observe the host element for child changes and class changes
        this.slotObserver.observe(this.el, {
            childList: true, // Watch for changes to child elements
            subtree: true,
            attributes: true, // Watch for attribute changes
            attributeFilter: ['class'], // Only watch class attribute changes
        });
    }
    render() {
        const hasMetaNavElements = this.el.querySelector('[slot="meta-nav"]') !== null;
        return (h("nav", { key: '87af039fdd229dd83657e9b5233fb439821e7236', class: {
                'main-nav': true,
                'is-mobile': this.isMobile,
                'is-open': this.isOpen,
            } }, h("div", { key: '8bfe109f21c1abddad23476c2bc984054c685f33', class: "main-nav-content" }, h("div", { key: 'd9661665e9f95469a0e416eb599b3ba8317d955d', class: {
                'main-nav-content-wrapper': true,
                overflow: this.navOverflow,
            } }, this.navOverflow && (h("dc-ui-button", { key: '8cd37e203ba60ec0a7716ce799a44ba4c423a402', class: "overflow-nav-btn", ref: (el) => (this.prevButton = el), variant: "link", size: "small", icon: "dc_backward_left", onClick: () => this.scrollNav('prev'), hideLabel: true }, this.overflowPrevLabel)), h("div", { key: 'c4da2b53957acff04e90f77e3a362b12f175e5b7', ref: (el) => (this.mainNavContentWrapperInner = el), class: {
                'main-nav-content-wrapper-inner': true,
                overflow: this.navOverflow,
            } }, h("slot", { key: '2f97e35f9a914ad17e2212b8cb2469c017a10c41' })), this.navOverflow && (h("dc-ui-button", { key: 'a17af6789793484eae58c7640da32eb1386218aa', class: "overflow-nav-btn", ref: (el) => (this.nextButton = el), variant: "link", size: "small", icon: "dc_forward_right", onClick: () => this.scrollNav('next'), hideLabel: true }, this.overflowNextLabel))), hasMetaNavElements && (h("div", { key: '80c2a1916767203f44adc9ba7dbbca32ad35f9a2', class: "meta-nav-wrapper" }, h("slot", { key: '8aa144260bf3cf62aa741ceb0762e01cc5cb9ec8', name: "meta-nav" }))))));
    }
    static get is() { return "dc-ui-header-main-nav"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["dc-ui-header-main-nav.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dc-ui-header-main-nav.css"]
        };
    }
    static get properties() {
        return {
            "maxEntries": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "11"
                        }],
                    "text": "The maximum number of entries in the main navigation before overflow is shown."
                },
                "getter": false,
                "setter": false,
                "attribute": "max-entries",
                "reflect": false,
                "defaultValue": "11"
            },
            "overflowNextLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "overflow-next-label",
                "reflect": false,
                "defaultValue": "'Next'"
            },
            "overflowPrevLabel": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "overflow-prev-label",
                "reflect": false,
                "defaultValue": "'Previous'"
            }
        };
    }
    static get states() {
        return {
            "isOpen": {},
            "isMobile": {},
            "navOverflow": {}
        };
    }
    static get methods() {
        return {
            "checkForOverflow": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "updateButtonStates": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "click",
                "method": "handleWindowClick",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "dcHeaderBurgerClick",
                "method": "handleBurgerClick",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=dc-ui-header-main-nav.js.map
